import React, { useState, useEffect } from "react";
import {
  BiCheck,
  BiChevronUp,
  BiLoaderAlt,
  BiMessage,
  BiTrendingDown,
  BiTrendingUp,
  BiX,
} from "react-icons/bi";
import { BsActivity, BsClock, BsEye } from "react-icons/bs";
import { FiAlertTriangle } from "react-icons/fi";
import { getSmsAnalytics } from "../../../../stores/actions/campaign.actions.types";
import { connect } from "react-redux";
import MessageListModal from "../../../Modals/message-table";

const getIcon = (key) => {
  switch (key) {
    case "total_sent":
      return <BiMessage size={20} className='text-gray-500' />;
    case "delivered":
      return <BiCheck size={20} className='text-gray-500' />;
    case "delivery_rate":
      return <BsActivity size={20} className='text-gray-500' />;
    case "failed":
      return <BiX size={20} className='text-gray-500' />;
    case "opt_outs":
      return <FiAlertTriangle size={20} className='text-gray-500' />;
    case "last_hour":
    case "last_12_hours":
      return <BsClock size={20} className='text-gray-500' />;
    default:
      return <div className='w-5 h-5' />;
  }
};

const MiniMetricCard = ({
  onClick,
  label,
  value,
  trend,
  trendDirection,
  secondaryText,
  status,
  statusColor,
  isSmall,
  iconKey,
}) => {
  let trendElement;
  let mainValueClass = isSmall ? "text-xl" : "text-2xl";

  if (trend !== undefined) {
    const TrendIcon = trendDirection === "up" ? BiTrendingUp : BiTrendingDown;
    const trendColor =
      trendDirection === "up"
        ? "text-green-500"
        : trendDirection === "down"
        ? "text-red-500"
        : "text-gray-400";

    trendElement = (
      <div className={`flex items-center text-sm font-medium ${trendColor}`}>
        <TrendIcon size={14} className='mr-1' />
        {typeof trend === "number" ? trend.toFixed(trend === 0 ? 3 : 2) : trend}
        {trendDirection !== "neutral" ? "%" : ""}
      </div>
    );
  } else if (status) {
    trendElement = (
      <div
        className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
          statusColor === "rate-bad"
            ? "bg-red-900/50 text-red-800 dark:text-red-100"
            : "bg-green-900/50 text-green-400"
        }`}
      >
        {status}
      </div>
    );
    mainValueClass =
      "text-2xl font-extrabold " +
      (statusColor === "rate-bad" ? "text-red-400" : "text-[#58a6ff]");
  }

  return (
    <div
      onClick={onClick}
      className='bg-white dark:bg-boxdark border cursor-pointer  border-gray-200 hover:border-red-600 p-6 rounded-xl shadow-md flex flex-col justify-between h-full'
    >
      <div className='flex justify-between items-center mb-4'>
        {getIcon(iconKey)}
        {trendElement}
      </div>

      <div className='flex-grow'>
        <p className='text-sm font-medium text-gray-800 dark:text-gray-300 mb-1'>
          {label}
        </p>
        <div
          className={`font-bold text-gray-900 dark:text-gray-100 ${mainValueClass}`}
        >
          {value ?? 0}
        </div>
      </div>

      <p className='text-xs text-gray-500 dark:text-gray-300 mt-2'>
        {secondaryText ?? ""}
      </p>
    </div>
  );
};

const ExtendedCard = ({
  onClick,
  label,
  value,
  context,
  secondaryText,
  icon,
}) => (
  <div
    onClick={onClick}
    className='bg-white dark:bg-boxdark border cursor-pointer  border-gray-200 hover:border-red-600 p-6 rounded-xl shadow-md h-full'
  >
    <div className='flex justify-between items-center mb-4'>
      {icon}
      <div className='flex items-center text-sm font-medium text-red-500'>
        <BiChevronUp size={14} className='mr-1' />
        {context ?? ""}
      </div>
    </div>
    <p className='text-sm font-medium text-gray-500 dark:text-gray-300 mb-1'>
      {label}
    </p>
    <div className='text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1'>
      {value ?? 0}
    </div>
    <p className='text-xs text-gray-500 dark:text-gray-300 mt-2'>
      {secondaryText ?? ""}
    </p>
  </div>
);

const transformMetrics = (raw) => {
  if (!raw) return null;

  const safeRate = (delivered = 0, total = 0) => {
    if (!total || total === 0) return "0%";
    return `${((delivered / total) * 100).toFixed(2)}%`;
  };

  // normalize possible field name variants: last24h vs last24
  const last24 = raw?.last24h ?? raw?.last24 ?? {};
  const last12 = raw?.last12h ?? raw?.last12 ?? {};
  const lastHour = raw?.lastHour ?? raw?.last_hour ?? {};
  const last7d = raw?.last7d ?? raw?.last_7d ?? {};
  const last14d = raw?.last14d ?? raw?.last_14d ?? {};

  // helper to choose rateColor (threshold 90%)
  const pickRateColor = (delivered = 0, total = 0) =>
    total === 0 || delivered / total < 0.9 ? "rate-bad" : "rate-good";

  return {
    last_24_hours: {
      total_sent: {
        value: last24?.totalSent ?? 0,
        trend: 0,
        trendDirection: "neutral",
        secondaryText: "Total messages sent",
      },
      delivered: {
        value: last24?.delivered ?? 0,
        trend: 0,
        trendDirection: "neutral",
        secondaryText: "Delivered messages",
      },
      delivery_rate: {
        value: safeRate(last24?.delivered ?? 0, last24?.totalSent ?? 0),
        status:
          (last24?.delivered ?? 0) / (last24?.totalSent || 1) < 0.9
            ? "Needs Work"
            : "Good",
        statusColor: pickRateColor(
          last24?.delivered ?? 0,
          last24?.totalSent ?? 0
        ),
        secondaryText: `${last24?.delivered ?? 0} delivered successfully`,
      },
      failed: {
        value: last24?.failed ?? 0,
        trend: 0,
        trendDirection: "neutral",
        secondaryText: "Failure count",
      },
      opt_outs: {
        value: last24?.optOuts?.length ?? last24?.opt_outs?.length ?? 0,
        trend: 0,
        trendDirection: "neutral",
        secondaryText: "Opt-out events",
      },
      last_hour: {
        value: lastHour?.totalSent ?? 0,
        trend: 0,
        trendDirection: "neutral",
        secondaryText: "Last hour volume",
      },
      last_12_hours: {
        value: last12?.totalSent ?? 0,
        trend: 0,
        trendDirection: "neutral",
        secondaryText: "Last 12 hours volume",
      },
    },

    extended_period: {
      last_7_days_sent: {
        value: last7d?.totalSent ?? 0,
        context: `+${last7d?.failed ?? 0} failed`,
        secondaryText: `Failure rate: ${safeRate(
          last7d?.failed ?? 0,
          last7d?.totalSent ?? 0
        )}`,
      },
      last_7_days_rate: {
        value: safeRate(last7d?.delivered ?? 0, last7d?.totalSent ?? 0),
        context: `+${last7d?.delivered ?? 0} delivered`,
        secondaryText: `${last7d?.totalSent ?? 0} sent total`,
      },
      last_14_days_sent: {
        value: last14d?.totalSent ?? 0,
        context: `+${last14d?.failed ?? 0} failed`,
        secondaryText: `Failure rate: ${safeRate(
          last14d?.failed ?? 0,
          last14d?.totalSent ?? 0
        )}`,
      },
      last_14_days_rate: {
        value: safeRate(last14d?.delivered ?? 0, last14d?.totalSent ?? 0),
        context: `+${last14d?.delivered ?? 0} delivered`,
        secondaryText: `${last14d?.totalSent ?? 0} sent total`,
      },
    },

    breakdown_data: [
      {
        period: "Last Hour",
        label: "lastHour",
        sent: lastHour?.totalSent ?? 0,
        delivered: lastHour?.delivered ?? 0,
        failed: lastHour?.failed ?? 0,
        undelivered: (lastHour?.totalSent ?? 0) - (lastHour?.delivered ?? 0),
        rate: safeRate(lastHour?.delivered ?? 0, lastHour?.totalSent ?? 0),
        rateColor: pickRateColor(
          lastHour?.delivered ?? 0,
          lastHour?.totalSent ?? 0
        ),
      },
      {
        period: "Last 12 Hours",
        label: "last12h",
        sent: last12?.totalSent ?? 0,
        delivered: last12?.delivered ?? 0,
        failed: last12?.failed ?? 0,
        undelivered: (last12?.totalSent ?? 0) - (last12?.delivered ?? 0),
        rate: safeRate(last12?.delivered ?? 0, last12?.totalSent ?? 0),
        rateColor: pickRateColor(
          last12?.delivered ?? 0,
          last12?.totalSent ?? 0
        ),
      },
      {
        period: "Last 24 Hours",
        label: "last24h",
        sent: last24?.totalSent ?? 0,
        delivered: last24?.delivered ?? 0,
        failed: last24?.failed ?? 0,
        undelivered: (last24?.totalSent ?? 0) - (last24?.delivered ?? 0),
        rate: safeRate(last24?.delivered ?? 0, last24?.totalSent ?? 0),
        rateColor: pickRateColor(
          last24?.delivered ?? 0,
          last24?.totalSent ?? 0
        ),
      },
      {
        period: "Last 7 Days",
        label: "last7d",
        sent: last7d?.totalSent ?? 0,
        delivered: last7d?.delivered ?? 0,
        failed: last7d?.failed ?? 0,
        undelivered: (last7d?.totalSent ?? 0) - (last7d?.delivered ?? 0),
        rate: safeRate(last7d?.delivered ?? 0, last7d?.totalSent ?? 0),
        rateColor: pickRateColor(
          last7d?.delivered ?? 0,
          last7d?.totalSent ?? 0
        ),
      },
      {
        period: "Last 14 Days",
        label: "last14d",
        sent: last14d?.totalSent ?? 0,
        delivered: last14d?.delivered ?? 0,
        failed: last14d?.failed ?? 0,
        undelivered: (last14d?.totalSent ?? 0) - (last14d?.delivered ?? 0),
        rate: safeRate(last14d?.delivered ?? 0, last14d?.totalSent ?? 0),
        rateColor: pickRateColor(
          last14d?.delivered ?? 0,
          last14d?.totalSent ?? 0
        ),
      },
    ],
  };
};

const SMSAnalytics = ({ getSmsAnalytics }) => {
  const [metrics, setMetrics] = useState(null);
  const [rawMetrics, setRawMetrics] = useState(null);
  const [selected, setSelected] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getSmsAnalytics({
      callback: (res) => {
        const transformed = transformMetrics(
          res?.data?.data ?? res?.data ?? res
        );
        setRawMetrics(res?.data?.data);
        setMetrics(transformed);
        setLoading(false);
      },
    });
  }, [getSmsAnalytics]);

  const handleClickedData = (e) => {
    const { label, status } = e;

    const periodData = rawMetrics?.[`${label}`];
    let messages = [];

    switch (status) {
      case "delivered":
        messages = periodData.deliveredMessages.map((msg) => ({
          ...msg,
          status: "Delivered",
        }));
        break;
      case "failed":
        messages = periodData.failedMessages.map((msg) => ({
          ...msg,
          status: "Failed",
        }));
        break;
      case "opt_outs":
        messages = periodData.optOuts.map((msg) => ({
          ...msg,
          status: "Opt-Out",
        }));
        break;
      case "total_sent":
        messages = [
          ...periodData.deliveredMessages.map((msg) => ({
            ...msg,
            status: "Delivered",
          })),
          ...periodData.failedMessages.map((msg) => ({
            ...msg,
            status: "Failed",
          })),
          ...periodData.optOuts.map((msg) => ({ ...msg, status: "Opt-Out" })),
        ];
        break;
      default:
        console.warn("Unknown status:", status);
    }

    if (messages?.length) {
      setSelected(messages);
      setModalOpen(true);
    }
  };

  const renderBreakdownTable = () => {
    if (!metrics?.breakdown_data || loading) {
      return (
        <tr>
          <td colSpan='6' className='py-4 text-center text-gray-500'>
            {loading ? "Fetching data..." : "No breakdown data available."}
          </td>
        </tr>
      );
    }

    return metrics?.breakdown_data?.map((row, index) => (
      <tr
        key={index}
        className='hover:bg-gray-50 dark:hover:bg-gray-900 transition duration-150'
      >
        <td className='px-6 py-3 text-sm text-gray-900 dark:text-gray-100'>
          {row?.period}
        </td>
        <td className='px-6 py-3 text-sm'>{row?.sent ?? 0}</td>
        <td className='px-6 py-3 text-sm'>{row?.delivered ?? 0}</td>
        <td className='px-6 py-3 text-sm'>{row?.failed ?? 0}</td>

        <td
          className={`px-6 py-3 text-sm font-semibold ${
            row?.rateColor === "rate-good" ? "text-green-600" : "text-red-600"
          }`}
        >
          {row?.rate ?? "0%"}
        </td>
        <td className='px-6 py-3 text-sm text-end'>
          <BsEye
            className='text-lg cursor-pointer'
            onClick={() =>
              handleClickedData({
                label: row?.label,
                status: "total_sent",
              })
            }
          />
        </td>
      </tr>
    ));
  };

  return (
    <div>
      {loading ? (
        <div className='flex rounded dark:bg-boxdark bg-white items-center justify-center min-h-96'>
          <BiLoaderAlt className='animate-spin text-4xl text-primary dark:text-white' />
        </div>
      ) : (
        <div className='min-h-screen p-3 bg-gray-50 dark:bg-gray-800 font-sans text-gray-800 dark:text-gray-200'>
          <style jsx global>{`
            .rate-good {
              color: #3fb950;
            }
            .rate-bad {
              color: #f85149;
            }
            .rate-neutral {
              color: #a0aec0;
            }
          `}</style>

          <div className='dashboard-content'>
            <header className='mb-8 border-b border-gray-200 dark:border-gray-700 pb-4'>
              <h1 className='text-2xl md:text-3xl font-bold mb-1'>
                SMS Marketing Dashboard
              </h1>
              <p className='text-md text-gray-500 dark:text-gray-400'>
                Monitor campaign performance and delivery metrics via Twilio.
              </p>
            </header>

            {!loading && metrics && (
              <>
                {/* Last 24 Hours */}
                <section className='mb-10'>
                  <h2 className='text-xl font-semibold mb-4'>Last 24 Hours</h2>

                  <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4'>
                    <MiniMetricCard
                      label='Total Sent'
                      iconKey='total_sent'
                      onClick={() =>
                        handleClickedData({
                          label: "last24h",
                          status: "total_sent",
                        })
                      }
                      value={metrics?.last_24_hours?.total_sent?.value}
                      {...metrics?.last_24_hours?.total_sent}
                    />
                    <MiniMetricCard
                      label='Delivered'
                      iconKey='delivered'
                      value={metrics?.last_24_hours?.delivered?.value}
                      {...metrics?.last_24_hours?.delivered}
                      onClick={() =>
                        handleClickedData({
                          label: "last24h",
                          status: "delivered",
                        })
                      }
                    />
                    <MiniMetricCard
                      label='Delivery Rate'
                      iconKey='delivery_rate'
                      value={metrics?.last_24_hours?.delivery_rate?.value}
                      {...metrics?.last_24_hours?.delivery_rate}
                    />
                    <MiniMetricCard
                      label='Failed'
                      iconKey='failed'
                      value={metrics?.last_24_hours?.failed?.value}
                      {...metrics?.last_24_hours?.failed}
                      onClick={() =>
                        handleClickedData({
                          label: "last24h",
                          status: "failed",
                        })
                      }
                    />
                    <MiniMetricCard
                      label='Opt-Outs (24h)'
                      iconKey='opt_outs'
                      value={metrics?.last_24_hours?.opt_outs?.value}
                      {...metrics?.last_24_hours?.opt_outs}
                      onClick={() =>
                        handleClickedData({
                          label: "last24h",
                          status: "opt_outs",
                        })
                      }
                    />
                  </div>

                  {/* Last Hour / Last 12 Hours */}
                  <div className='grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-3 gap-4 mt-4'>
                    <MiniMetricCard
                      label='Delivery Rate (24h)'
                      iconKey='delivery_rate'
                      value={metrics?.last_24_hours?.delivery_rate?.value}
                      status={metrics?.last_24_hours?.delivery_rate?.status}
                      statusColor={
                        metrics?.last_24_hours?.delivery_rate?.statusColor
                      }
                      secondaryText={
                        metrics?.last_24_hours?.delivery_rate?.secondaryText
                      }
                    />

                    <MiniMetricCard
                      label='Last Hour'
                      iconKey='last_hour'
                      isSmall={true}
                      value={metrics?.last_24_hours?.last_hour?.value}
                      {...metrics?.last_24_hours?.last_hour}
                      onClick={() =>
                        handleClickedData({
                          label: "lastHour",
                          status: "total_sent",
                        })
                      }
                    />

                    <MiniMetricCard
                      label='Last 12 Hours'
                      iconKey='last_12_hours'
                      isSmall={true}
                      value={metrics?.last_24_hours?.last_12_hours?.value}
                      {...metrics?.last_24_hours?.last_12_hours}
                      onClick={() =>
                        handleClickedData({
                          label: "last12h",
                          status: "total_sent",
                        })
                      }
                    />
                  </div>
                </section>

                {/* Extended Period */}
                <section className='mb-10'>
                  <h2 className='text-xl font-semibold mb-4'>
                    Extended Period Overview
                  </h2>
                  <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'>
                    <ExtendedCard
                      label='Last 7 Days - Sent'
                      icon={<BiMessage size={20} className='text-gray-500' />}
                      value={metrics?.extended_period?.last_7_days_sent?.value}
                      context={
                        metrics?.extended_period?.last_7_days_sent?.context
                      }
                      secondaryText={
                        metrics?.extended_period?.last_7_days_sent
                          ?.secondaryText
                      }
                      onClick={() =>
                        handleClickedData({
                          label: "last7d",
                          status: "total_sent",
                        })
                      }
                    />
                    <ExtendedCard
                      label='Last 7 Days - Delivery Rate'
                      icon={<BsActivity size={20} className='text-gray-500' />}
                      value={metrics?.extended_period?.last_7_days_rate?.value}
                      context={
                        metrics?.extended_period?.last_7_days_rate?.context
                      }
                      secondaryText={
                        metrics?.extended_period?.last_7_days_rate
                          ?.secondaryText
                      }
                      onClick={() =>
                        handleClickedData({
                          label: "last7d",
                          status: "delivered",
                        })
                      }
                    />
                    <ExtendedCard
                      label='Last 14 Days - Sent'
                      icon={<BiMessage size={20} className='text-gray-500' />}
                      value={metrics?.extended_period?.last_14_days_sent?.value}
                      context={
                        metrics?.extended_period?.last_14_days_sent?.context
                      }
                      secondaryText={
                        metrics?.extended_period?.last_14_days_sent
                          ?.secondaryText
                      }
                      onClick={() =>
                        handleClickedData({
                          label: "last14d",
                          status: "total_sent",
                        })
                      }
                    />
                    <ExtendedCard
                      label='Last 14 Days - Delivery Rate'
                      icon={<BsActivity size={20} className='text-gray-500' />}
                      value={metrics?.extended_period?.last_14_days_rate?.value}
                      context={
                        metrics?.extended_period?.last_14_days_rate?.context
                      }
                      secondaryText={
                        metrics?.extended_period?.last_14_days_rate
                          ?.secondaryText
                      }
                    />
                  </div>
                </section>

                {/* Breakdown Table */}
                <section className='mt-12'>
                  <h2 className='text-xl font-semibold mb-4'>
                    All Periods Breakdown
                  </h2>
                  <div className='overflow-x-auto bg-white dark:bg-gray-800 border-gray-200 rounded-xl shadow-md'>
                    <table className='min-w-full divide-y divide-gray-200'>
                      <thead className='bg-gray-50 dark:bg-gray-900'>
                        <tr>
                          <th className='px-6 py-3 text-left text-xs font-medium uppercase'>
                            Period
                          </th>
                          <th className='px-6 py-3 text-left text-xs font-medium uppercase'>
                            Total Sent
                          </th>
                          <th className='px-6 py-3 text-left text-xs font-medium uppercase'>
                            Delivered
                          </th>
                          <th className='px-6 py-3 text-left text-xs font-medium uppercase'>
                            Failed
                          </th>

                          <th className='px-6 py-3 text-left text-xs font-medium uppercase'>
                            Delivery Rate
                          </th>
                          <th className='px-6 py-3 text-left text-xs font-medium uppercase'>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className='divide-y divide-gray-200'>
                        {renderBreakdownTable()}
                      </tbody>
                    </table>
                  </div>
                </section>
              </>
            )}
          </div>
        </div>
      )}
      <MessageListModal
        messages={selected}
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  loading: state?.app?.visible ?? false,
});

export default connect(mapStateToProps, { getSmsAnalytics })(SMSAnalytics);
